import React, { FC } from 'react';
import styled from 'styled-components';
import { forTablet, forIE, forDesktop } from '@westwing/ui-kit/breakPoints';
import { useLooksTracking } from '../hooks/useLooksTracking';
import ErrorBoundary from 'Client/components/Generic/ErrorBoundaries';
import GenericErrorRetry from 'Client/components/Generic/ErrorPages/GenericErrorRetry';
import Product from 'Client/components/Product/GenericProduct';
import { ProductInterface, WishlistTrackingParameter } from 'Client/redux/products/types';
import { HeroProduct } from 'Client/components/Product';
import { TrackLooksActions } from 'Common/ga/types';

const StyledWrapper = styled.div`
    padding: 10px;
    border-top: 1px solid ${({ theme }) => theme.colors.charcoal20};

    ${forTablet`
        margin-top: 40px;
        padding: 10px 0;
    `}

    ${forDesktop`
          margin-bottom: 53px;
    `}
`;

const StyledGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 5px;

    ${forTablet`
        grid-template-columns: repeat(3, 1fr);
        grid-gap: 10px;
    `}

    ${forDesktop`
        grid-template-columns: repeat(4, 1fr);
        grid-gap: 10px;
    `}

    ${forIE`
        display: flex;
        flex-flow: row wrap;
        justify-content: space-around;
    `}
`;

/**
 * This array contains the position starting from '0' in which we should have the Hero Product
 */
const heroProductsSpotsInGrid: number[] = [6, 15, 26, 33, 42, 53];
export const isHeroProduct = (position: number): boolean => heroProductsSpotsInGrid.includes(position);

export interface ProductListingProps {
    lookTrackingName: string;
    products: ProductInterface[];
}

const ProductListing: FC<ProductListingProps> = ({ lookTrackingName, products }) => {
    const { LDP } = useLooksTracking();

    const clickTrackingFn = (sku: string) => {
        LDP.track(TrackLooksActions.LDPProductClick, `Look: ${lookTrackingName}, Product: ${sku}`);
    };

    const wishListTrackingFn = ({ action, sku, category, name, brand, price }: WishlistTrackingParameter) => {
        LDP.trackWishlist({ action, sku, category, name, brand, price });
    };

    return (
        <StyledWrapper>
            <StyledGrid>
                <ErrorBoundary boundaryName="LooksProductListing" fallbackComponent={<GenericErrorRetry />}>
                    {products.map(product => {
                        const isHero = isHeroProduct(product.position);

                        const props = {
                            product: {
                                ...product,
                                clickTrackingFn: () => clickTrackingFn(product.sku),
                                wishListTrackingFn,
                            },
                            isHeroProduct: isHero,
                        };

                        return isHero ? (
                            <HeroProduct {...props} key={product.position} flexBasisDivisor={4} />
                        ) : (
                            <Product {...props} key={product.position} flexBasisDivisor={4} />
                        );
                    })}
                </ErrorBoundary>
            </StyledGrid>
        </StyledWrapper>
    );
};

export default ProductListing;
